import { useState } from 'react';
import type { FormEvent } from 'react';
import { AdminShell } from './AdminShell';
import { useBranding } from '../../context/branding';
import { ApiError, brandingApi } from '../../lib/api';
import { APP_NAME, BRAND_BACKDROP, BRAND_LOGO, BRAND_NAME } from '../../lib/brand';

/** The server takes a picture of this size at most, once it is base64. */
const IMAGE_MAX_BYTES = 300 * 1024;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'];

function readImage(file: File): Promise<string> {
  if (!IMAGE_TYPES.includes(file.type)) {
    return Promise.reject(new Error('Use a PNG, JPEG, WebP, GIF or SVG picture.'));
  }
  if (file.size > IMAGE_MAX_BYTES) {
    return Promise.reject(new Error(`That picture is ${Math.round(file.size / 1024)} KB; use one under 300 KB.`));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.readAsDataURL(file);
  });
}

/**
 * /web/Admin/Customization — the names and pictures the portal shows: the
 * operator's name and logo in the navbar and on the sign-in page, the product
 * name in the browser tab and the footer, the sign-in backdrop. Saved on the
 * server, so it changes for everyone without a rebuild. An empty field keeps
 * the value the client was built with.
 */
export function Customization() {
  const { saved, apply } = useBranding();

  const [brandName, setBrandName] = useState(saved?.brandName ?? '');
  const [appName, setAppName] = useState(saved?.appName ?? '');
  const [logo, setLogo] = useState(saved?.logo ?? '');
  const [backdrop, setBackdrop] = useState(saved?.backdrop ?? '');
  const [showName, setShowName] = useState(saved?.showName ?? false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // What the preview shows: the form, or the built-in value where it is empty.
  const shown = {
    brandName: brandName.trim() || BRAND_NAME,
    appName: appName.trim() || APP_NAME,
    logo: logo || BRAND_LOGO,
    backdrop: backdrop || BRAND_BACKDROP,
  };

  const pick = (set: (v: string) => void) => async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      set(await readImage(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that file.');
    }
  };

  async function save(body: { brandName: string; appName: string; logo: string; backdrop: string; showName: boolean }) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await brandingApi.save(body);
      apply(next);
      setNotice('Saved. Everyone sees the change on their next page load.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the changes.');
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void save({ brandName, appName, logo, backdrop, showName });
  }

  function resetAll() {
    setBrandName('');
    setAppName('');
    setLogo('');
    setBackdrop('');
    setShowName(false);
    void save({ brandName: '', appName: '', logo: '', backdrop: '', showName: false });
  }

  return (
    <AdminShell active="Customization" title="Customization">
      {error && (
        <div className="alert cmmsg" role="alert">
          {error}
        </div>
      )}
      {notice && !error && (
        <p className="kbnotice cmmsg" role="status">
          {notice}
        </p>
      )}

      <div className="brandadmin">
        <form className="brandform" onSubmit={onSubmit}>
          <label className="cmfield brandfield">
            <span>Operator name</span>
            <input

              value={brandName}
              maxLength={40}
              placeholder={BRAND_NAME}
              onChange={(e) => setBrandName(e.target.value)}
            />
            <small>The large wordmark on the sign-in page, and the logo's description.</small>
          </label>

          <label className="cmfield brandfield">
            <span>Product name</span>
            <input

              value={appName}
              maxLength={40}
              placeholder={APP_NAME}
              onChange={(e) => setAppName(e.target.value)}
            />
            <small>Beside the logo in the navbar, the browser tab, the low-bandwidth option and the footer.</small>
          </label>

          <ImageField
            label="Logo"
            hint="Navbar and sign-in page. PNG, JPEG, WebP, GIF or SVG, under 300 KB."
            value={logo}
            fallback={BRAND_LOGO}
            onPick={pick(setLogo)}
            onClear={() => setLogo('')}
          />

          <label className="brandcheck">
            <input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} />
            Show the product name beside the logo in the navbar
          </label>

          <ImageField
            label="Sign-in backdrop"
            hint="The picture behind the sign-in card. Under 300 KB."
            value={backdrop}
            fallback={BRAND_BACKDROP}
            onPick={pick(setBackdrop)}
            onClear={() => setBackdrop('')}
          />

          <div className="brandbuttons">
            <button type="submit" className="cmbtn cmbtn--primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" className="cmbtn" disabled={busy || !saved} onClick={resetAll}>
              Back to built-in
            </button>
            {saved?.updatedBy && (
              <small className="brandmeta">
                Last saved by {saved.updatedBy}
                {saved.updatedAt && ` on ${new Date(saved.updatedAt).toLocaleString()}`}
              </small>
            )}
          </div>
        </form>

        <aside className="brandpreview" aria-label="Preview">
          <h2>Navbar</h2>
          <div className="brandpreview-bar">
            {shown.logo ? (
              <span className="brand">
                <img className="brand-img" src={shown.logo} alt="" height={26} />
                {showName && <span className="brand-name">{shown.appName}</span>}
              </span>
            ) : (
              <span className="brand-name">{shown.appName}</span>
            )}
          </div>

          <h2>Sign-in page</h2>
          <div
            className="brandpreview-signin"
            style={shown.backdrop ? { backgroundImage: `url("${shown.backdrop}")` } : undefined}
          >
            <div className="brandpreview-card">
              <strong>{shown.brandName}</strong>
              {shown.logo && <img src={shown.logo} alt="" height={28} />}
            </div>
            <small>Copyright © {new Date().getFullYear()} {shown.appName}</small>
          </div>
        </aside>
      </div>
    </AdminShell>
  );
}

function ImageField({
  label,
  hint,
  value,
  fallback,
  onPick,
  onClear,
}: {
  label: string;
  hint: string;
  value: string;
  fallback: string;
  onPick: (file: File | undefined) => void;
  onClear: () => void;
}) {
  const src = value || fallback;
  return (
    <div className="cmfield brandfield">
      <span>{label}</span>
      <div className="brandimage">
        <span className="brandimage-box">{src ? <img src={src} alt="" /> : <em>None</em>}</span>
        <label className="cmbtn cmbtn--small">
          Choose…
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            className="visually-hidden"
            onChange={(e) => {
              onPick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
        {value && (
          <button type="button" className="cmbtn cmbtn--small" onClick={onClear}>
            Use built-in
          </button>
        )}
      </div>
      <small>{value ? 'Uploaded here.' : fallback ? 'Built-in picture.' : 'No picture: a drawn mark is shown.'} {hint}</small>
    </div>
  );
}
